// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Video Poker (Jacks or Better)
// Classic 5-card draw with provably fair card dealing
// ================================================================
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ProvablyFairEngine from '../../engine/ProvablyFairEngine';
import { useCasino } from '../../context/CasinoShell';

const SUITS = ['♠', '♥', '♦', '♣'];
const SUIT_COLORS = { '♠': '#E8E8E8', '♥': '#EF4444', '♦': '#3B82F6', '♣': '#22C55E' };
const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
const RANK_VALUES = { '2':2,'3':3,'4':4,'5':5,'6':6,'7':7,'8':8,'9':9,'10':10,'J':11,'Q':12,'K':13,'A':14 };

// Jacks or Better paytable (per coin)
const PAYTABLE = [
  { name: 'Royal Flush',     multiplier: 250 },
  { name: 'Straight Flush',  multiplier: 50 },
  { name: 'Four of a Kind',  multiplier: 25 },
  { name: 'Full House',      multiplier: 9 },
  { name: 'Flush',           multiplier: 6 },
  { name: 'Straight',        multiplier: 4 },
  { name: 'Three of a Kind', multiplier: 3 },
  { name: 'Two Pair',        multiplier: 2 },
  { name: 'Jacks or Better', multiplier: 1 },
];

function buildDeck() {
  const deck = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({ suit, rank, value: RANK_VALUES[rank] });
    }
  }
  return deck;
}

function evaluateHand(hand) {
  const sorted = [...hand].sort((a, b) => a.value - b.value);
  const ranks = sorted.map(c => c.value);
  const suits = sorted.map(c => c.suit);
  const isFlush = suits.every(s => s === suits[0]);

  // Check straight (including A-low: A,2,3,4,5)
  let isStraight = false;
  const unique = [...new Set(ranks)];
  if (unique.length === 5) {
    if (unique[4] - unique[0] === 4) isStraight = true;
    if (unique[0] === 2 && unique[1] === 3 && unique[2] === 4 && unique[3] === 5 && unique[4] === 14) isStraight = true;
  }

  // Count ranks
  const counts = {};
  for (const r of ranks) counts[r] = (counts[r] || 0) + 1;
  const countValues = Object.values(counts).sort((a, b) => b - a);

  // Royal Flush
  if (isFlush && isStraight && ranks.includes(14) && ranks.includes(13)) return { hand: 'Royal Flush', index: 0 };
  if (isFlush && isStraight) return { hand: 'Straight Flush', index: 1 };
  if (countValues[0] === 4) return { hand: 'Four of a Kind', index: 2 };
  if (countValues[0] === 3 && countValues[1] === 2) return { hand: 'Full House', index: 3 };
  if (isFlush) return { hand: 'Flush', index: 4 };
  if (isStraight) return { hand: 'Straight', index: 5 };
  if (countValues[0] === 3) return { hand: 'Three of a Kind', index: 6 };
  if (countValues[0] === 2 && countValues[1] === 2) return { hand: 'Two Pair', index: 7 };
  if (countValues[0] === 2) {
    // Check if pair is Jacks or better
    const pairRank = parseInt(Object.entries(counts).find(([, v]) => v === 2)?.[0] || '0');
    if (pairRank >= 11) return { hand: 'Jacks or Better', index: 8 };
  }
  return { hand: 'No Win', index: -1 };
}

const BET_OPTIONS = [1, 2, 5, 10, 25];

export default function VideoPoker() {
  const { forgeBalance, placeBet, recordWin, recordLoss, logFairness, isExcluded } = useCasino();
  const engineRef = useRef(new ProvablyFairEngine());

  const [bet, setBet] = useState(5);
  const [hand, setHand] = useState([null, null, null, null, null]);
  const [held, setHeld] = useState([false, false, false, false, false]);
  const [deck, setDeck] = useState([]);
  const [gameState, setGameState] = useState('betting'); // betting, holding, resolved
  const [result, setResult] = useState(null);
  const [lastWin, setLastWin] = useState(null);
  const [commitmentHash, setCommitmentHash] = useState(null);
  const [dealAnim, setDealAnim] = useState(false);
  const [drawAnim, setDrawAnim] = useState(false);
  const [history, setHistory] = useState([]);
  const [winHighlight, setWinHighlight] = useState(-1);

  useEffect(() => {
    async function init() {
      const commitment = await engineRef.current.createCommitment();
      setCommitmentHash(commitment.commitmentHash);
    }
    init();
  }, []);

  const shuffleDeck = (hash) => {
    const d = buildDeck();
    for (let i = d.length - 1; i > 0; i--) {
      const hexPos = ((d.length - 1 - i) * 4) % (hash.length - 4);
      const j = parseInt(hash.slice(hexPos, hexPos + 4), 16) % (i + 1);
      [d[i], d[j]] = [d[j], d[i]];
    }
    return d;
  };

  const dealCards = useCallback(async () => {
    if (gameState !== 'betting') return;
    if (isExcluded()) return;

    const betResult = placeBet(bet, 'video-poker');
    if (!betResult.success) return;

    const clientSeed = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    const fairResult = await engineRef.current.generateResult(clientSeed);
    const proof = engineRef.current.getProof();

    const shuffled = shuffleDeck(fairResult.resultHash);

    setDealAnim(true);
    setHeld([false, false, false, false, false]);
    setResult(null);
    setLastWin(null);
    setWinHighlight(-1);

    // Animate deal
    const newHand = shuffled.slice(0, 5);
    for (let i = 0; i < 5; i++) {
      await new Promise(r => setTimeout(r, 150));
      setHand(prev => {
        const next = [...prev];
        next[i] = newHand[i];
        return next;
      });
    }

    setDeck(shuffled.slice(5));
    setDealAnim(false);
    setGameState('holding');

    logFairness({ gameId: 'video-poker', ...proof, betAmount: bet });
  }, [gameState, bet, placeBet, isExcluded, logFairness]);

  const toggleHold = (idx) => {
    if (gameState !== 'holding') return;
    setHeld(prev => {
      const next = [...prev];
      next[idx] = !next[idx];
      return next;
    });
  };

  const drawCards = useCallback(async () => {
    if (gameState !== 'holding') return;

    setDrawAnim(true);
    let newHand = [...hand];
    let deckCopy = [...deck];

    // Replace non-held cards
    for (let i = 0; i < 5; i++) {
      if (!held[i]) {
        await new Promise(r => setTimeout(r, 150));
        newHand[i] = deckCopy[0];
        deckCopy = deckCopy.slice(1);
        setHand([...newHand]);
      }
    }

    setDrawAnim(false);

    // Evaluate
    const eval_ = evaluateHand(newHand);
    const payEntry = eval_.index >= 0 ? PAYTABLE[eval_.index] : null;
    const winAmount = payEntry ? payEntry.multiplier * bet : 0;

    if (winAmount > 0) {
      recordWin(winAmount, 'video-poker');
      setLastWin(winAmount);
      setWinHighlight(eval_.index);
    } else {
      recordLoss(bet, 'video-poker');
    }

    setResult({ ...eval_, winAmount });
    setHistory(prev => [{
      hand: eval_.hand,
      bet,
      win: winAmount,
      time: new Date().toLocaleTimeString(),
    }, ...prev].slice(0, 15));

    setGameState('resolved');

    const nextCommitment = await engineRef.current.createCommitment();
    setCommitmentHash(nextCommitment.commitmentHash);
  }, [gameState, hand, held, deck, bet, recordWin, recordLoss]);

  const newGame = useCallback(async () => {
    setGameState('betting');
    setHand([null, null, null, null, null]);
    setHeld([false, false, false, false, false]);
    setResult(null);
    setWinHighlight(-1);
  }, []);

  const renderCard = (card, index) => {
    if (!card) {
      return (
        <div key={index} style={cs.cardSlot}>
          <div style={cs.emptyCard} />
        </div>
      );
    }
    const isRed = card.suit === '♥' || card.suit === '♦';
    const isHeld = held[index] && gameState === 'holding';

    return (
      <div key={index} style={cs.cardSlot} onClick={() => toggleHold(index)}>
        {isHeld && <div style={cs.holdBadge}>HOLD</div>}
        <div style={{
          ...cs.card,
          border: isHeld ? '2px solid #FFD700' : '1px solid #334155',
          boxShadow: isHeld ? '0 0 12px #FFD70044' : '0 4px 12px rgba(0,0,0,0.3)',
          transform: isHeld ? 'translateY(-4px)' : 'translateY(0)',
        }}>
          <span style={{ ...cs.cardRankTop, color: isRed ? '#EF4444' : '#E8E8E8' }}>{card.rank}</span>
          <span style={{ ...cs.cardSuitCenter, color: SUIT_COLORS[card.suit] }}>{card.suit}</span>
          <span style={{ ...cs.cardRankBot, color: isRed ? '#EF4444' : '#E8E8E8' }}>{card.rank}</span>
        </div>
      </div>
    );
  };

  return (
    <div style={cs.container}>
      <style>{`
        @keyframes pokerFlip { 0%{transform:rotateY(90deg);opacity:0} 100%{transform:rotateY(0);opacity:1} }
        @keyframes pokerWin { 0%,100%{background:#FFD70033} 50%{background:#FFD70011} }
      `}</style>

      {/* Header */}
      <div style={cs.header}>
        <h2 style={cs.title}>VIDEO POKER</h2>
        <div style={cs.subtitle}>JACKS OR BETTER</div>
      </div>

      {/* Paytable */}
      <div style={cs.paytable}>
        {PAYTABLE.map((p, i) => (
          <div key={i} style={{
            ...cs.payRow,
            background: winHighlight === i ? '#FFD70033' : (i % 2 === 0 ? 'rgba(15,23,42,0.5)' : 'transparent'),
            animation: winHighlight === i ? 'pokerWin 1s ease-in-out infinite' : 'none',
          }}>
            <span style={{
              ...cs.payName,
              color: winHighlight === i ? '#FFD700' : '#94A3B8',
            }}>{p.name}</span>
            <span style={{
              ...cs.payMulti,
              color: winHighlight === i ? '#FFD700' : '#F8FAFC',
            }}>{p.multiplier}x</span>
          </div>
        ))}
      </div>

      {/* Cards */}
      <div style={cs.cardArea}>
        <div style={cs.cardsRow}>
          {hand.map((card, i) => renderCard(card, i))}
        </div>
        {gameState === 'holding' && (
          <div style={cs.holdHint}>Tap cards to hold, then draw</div>
        )}
      </div>

      {/* Result */}
      {result && (
        <div style={{
          ...cs.resultBar,
          background: result.winAmount > 0
            ? 'linear-gradient(135deg, #14532D 0%, #166534 100%)'
            : 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
          borderColor: result.winAmount > 0 ? '#22C55E' : '#475569',
        }}>
          <span style={cs.resultHand}>{result.hand}</span>
          {result.winAmount > 0 && (
            <span style={cs.resultWin}>+${result.winAmount.toFixed(2)}</span>
          )}
        </div>
      )}

      {/* Controls */}
      <div style={cs.controls}>
        {gameState === 'betting' && (
          <>
            <div style={cs.betRow}>
              <span style={cs.betLabel}>BET</span>
              <div style={cs.betBtns}>
                {BET_OPTIONS.map(b => (
                  <button key={b} style={{
                    ...cs.betBtn,
                    background: bet === b ? '#DC2626' : '#1E293B',
                    borderColor: bet === b ? '#EF4444' : '#334155',
                  }} onClick={() => setBet(b)}>${b}</button>
                ))}
              </div>
            </div>
            <div style={cs.balRow}>
              <span style={cs.balLabel}>BALANCE</span>
              <span style={cs.balVal}>${forgeBalance.toFixed(2)}</span>
            </div>
            <button style={{
              ...cs.mainBtn,
              opacity: forgeBalance < bet ? 0.4 : 1,
            }} onClick={dealCards} disabled={forgeBalance < bet}>
              DEAL • ${bet.toFixed(2)}
            </button>
          </>
        )}

        {gameState === 'holding' && (
          <button style={{ ...cs.mainBtn, background: 'linear-gradient(180deg, #2563EB 0%, #1D4ED8 100%)', borderColor: '#3B82F6' }}
            onClick={drawCards}>
            DRAW
          </button>
        )}

        {gameState === 'resolved' && (
          <button style={{ ...cs.mainBtn, background: 'linear-gradient(180deg, #16A34A 0%, #15803D 100%)', borderColor: '#22C55E' }}
            onClick={newGame}>
            NEW HAND
          </button>
        )}
      </div>

      {/* Footer */}
      <div style={cs.footer}>
        <span>Jacks or Better • RTP: ~99.54% • Provably Fair SHA-256</span>
      </div>
    </div>
  );
}

const cs = {
  container: { fontFamily: "'SF Pro Display', -apple-system, sans-serif" },
  header: {
    padding: '14px 16px',
    background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
    borderBottom: '1px solid #334155',
    textAlign: 'center',
  },
  title: { fontSize: 16, fontWeight: 800, color: '#F8FAFC', letterSpacing: 4, margin: 0 },
  subtitle: { fontSize: 9, color: '#7C3AED', fontWeight: 700, letterSpacing: 3, marginTop: 2 },
  paytable: {
    padding: '6px 12px',
    background: 'linear-gradient(180deg, #1E293B 0%, #0F172A 100%)',
    borderBottom: '2px solid #7C3AED33',
  },
  payRow: {
    display: 'flex', justifyContent: 'space-between',
    padding: '3px 10px', borderRadius: 4,
  },
  payName: { fontSize: 10, fontWeight: 600 },
  payMulti: { fontSize: 10, fontWeight: 800, fontFamily: 'monospace' },
  cardArea: {
    padding: '20px 8px',
    background: 'linear-gradient(180deg, #14532D 0%, #166534 100%)',
    borderTop: '3px solid #854D0E',
    borderBottom: '3px solid #854D0E',
  },
  cardsRow: {
    display: 'flex', justifyContent: 'center', gap: 6,
  },
  cardSlot: {
    position: 'relative', cursor: 'pointer',
  },
  holdBadge: {
    position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
    padding: '1px 8px', borderRadius: 8,
    background: '#FFD700', color: '#000',
    fontSize: 7, fontWeight: 900, letterSpacing: 1,
    zIndex: 2,
  },
  card: {
    width: 58, height: 82,
    borderRadius: 8,
    background: 'linear-gradient(180deg, #FFFFFF 0%, #F1F5F9 100%)',
    border: '1px solid #CBD5E1',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'space-between',
    padding: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    transition: 'all 0.2s ease',
  },
  emptyCard: {
    width: 58, height: 82, borderRadius: 8,
    background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
    border: '2px solid #3B82F6',
    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
  },
  cardRankTop: { fontSize: 14, fontWeight: 900, fontFamily: 'Georgia, serif', alignSelf: 'flex-start' },
  cardSuitCenter: { fontSize: 22 },
  cardRankBot: { fontSize: 14, fontWeight: 900, fontFamily: 'Georgia, serif', alignSelf: 'flex-end', transform: 'rotate(180deg)' },
  holdHint: {
    textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,0.5)',
    marginTop: 10, fontWeight: 600, letterSpacing: 1,
  },
  resultBar: {
    margin: '0 16px', padding: '10px 16px',
    borderRadius: 10, border: '2px solid',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: -8, position: 'relative', zIndex: 2,
  },
  resultHand: { fontSize: 13, fontWeight: 900, color: '#F8FAFC', letterSpacing: 1 },
  resultWin: { fontSize: 14, fontWeight: 900, color: '#FFD700' },
  controls: {
    padding: '16px',
    background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
  },
  betRow: { marginBottom: 10 },
  betLabel: { fontSize: 8, color: '#64748B', fontWeight: 700, letterSpacing: 2, display: 'block', marginBottom: 6, textAlign: 'center' },
  betBtns: { display: 'flex', justifyContent: 'center', gap: 6 },
  betBtn: {
    padding: '6px 14px', borderRadius: 8,
    border: '2px solid', color: '#F8FAFC',
    fontSize: 11, fontWeight: 800, cursor: 'pointer',
    transition: 'all 0.15s',
  },
  balRow: {
    display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginBottom: 12,
  },
  balLabel: { fontSize: 8, color: '#64748B', fontWeight: 700, letterSpacing: 1 },
  balVal: { fontSize: 16, fontWeight: 800, color: '#FFD700', fontFamily: 'monospace' },
  mainBtn: {
    width: '100%', padding: '14px',
    borderRadius: 12, border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #B91C1C 100%)',
    color: '#fff', fontSize: 14, fontWeight: 900,
    letterSpacing: 3, cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
  },
  footer: {
    padding: '10px 16px', textAlign: 'center',
    fontSize: 8, color: '#475569', letterSpacing: 1,
    background: '#0F172A',
  },
};
